import React, { useState, useEffect, useRef } from "react";
import { ScrollView, TouchableOpacity, Text as RNText } from "react-native";
import { View } from "react-native";
import { useTheme } from "@react-navigation/native";
import { Theme } from "@theme";
import { StyleSheet } from "react-native";
import { SelectionItem } from "@utils";
import { ContextualDropdown } from "../molecules/ContextualDropdown";
import { PickerModal } from "../molecules/PickerModal";
import { Text } from "../atoms";
import { useKeepContactStore } from "@store";
import { useLanguage } from "@context";
import DatePicker from "react-native-date-picker";
import dayjs from 'dayjs'
import { defaultCustomRecurrence, type CustomRecurrenceState } from "@utils"

const FREQ_IDS = ['day', 'week', 'month', 'year'] as const;

export const Recurrence = ({
  initialCustom,
  dtstart,
  onValidate,
}: {
  initialCustom?: CustomRecurrenceState | null
  dtstart: Date
  onValidate: (custom: CustomRecurrenceState) => void
}) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { weekDays } = useKeepContactStore()
  const { t } = useLanguage()

  const FREQ_OPTIONS: SelectionItem[] = [
    { id: 'day', name: t('recurrenceComponent.everyDay') },
    { id: 'week', name: t('recurrenceComponent.everyWeek') },
    { id: 'month', name: t('recurrenceComponent.everyMonth') },
    { id: 'year', name: t('recurrenceComponent.everyYear') },
  ];

  const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => t('calendarModal.month' + i).slice(0, 4));

  const [repeatUnit, setRepeatUnit] = useState<SelectionItem>(() =>
    initialCustom ? FREQ_OPTIONS.find(o => o.id === initialCustom.freq) ?? FREQ_OPTIONS[0] : FREQ_OPTIONS[0]
  )
  const [repeatFrequency, setRepeatFrequency] = useState<SelectionItem>(() =>
    initialCustom ? { id: String(initialCustom.interval), name: String(initialCustom.interval) } : { id: '1', name: '1' }
  )
  const [selectedDays, setSelectedDays] = useState<number[]>(() => initialCustom?.byMonthDay ?? [])
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() =>
    initialCustom?.byMonth?.map(m => MONTH_LABELS[m - 1]) ?? []
  )
  const [endType, setEndType] = useState<'Jamais' | 'Le' | 'Après'>(() => {
    if (!initialCustom) return 'Jamais'
    return initialCustom.endType === 'until' ? 'Le' : initialCustom.endType === 'count' ? 'Après' : 'Jamais'
  })
  const [endDate, setEndDate] = useState<Date>(() => initialCustom?.until ?? dayjs().add(1, 'year').toDate())
  const [endCount, setEndCount] = useState<SelectionItem>(() =>
    initialCustom?.count ? { id: String(initialCustom.count), name: String(initialCustom.count) } : { id: '10', name: '10' }
  )
  const [isDatePickerModalVisible, setIsDatePickerModalVisible] = useState(false)
  const [isPickerEndRepeatModalVisible, setIsPickerEndRepeatModalVisible] = useState(false)
  const [selectedWeekDays, setSelectedWeekDays] = useState<number[]>(() =>
    initialCustom?.byDay?.length ? initialCustom.byDay : [dtstart.getDay()]
  )

  const isFirstRender = useRef(true)
  const onValidateRef = useRef(onValidate)
  onValidateRef.current = onValidate

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const freq = repeatUnit.id as 'day' | 'week' | 'month' | 'year'
    const interval = parseInt(repeatFrequency.id, 10) || 1
    const byDay = repeatUnit.id === 'week' ? (selectedWeekDays.length ? selectedWeekDays : [dtstart.getDay()]) : []
    const byMonthDay = ['month', 'year'].includes(repeatUnit.id) ? (selectedDays.length ? selectedDays : [dtstart.getDate()]) : []
    const byMonth = repeatUnit.id === 'year'
      ? (selectedMonths.length ? selectedMonths.map(m => MONTH_LABELS.indexOf(m) + 1).filter(n => n > 0) : [dtstart.getMonth() + 1])
      : []
    const endTypeMapped = endType === 'Le' ? 'until' : endType === 'Après' ? 'count' : 'never'
    const until = endType === 'Le' ? endDate : null
    const count = endType === 'Après' ? parseInt(endCount.id, 10) || 10 : 0
    onValidateRef.current({
      ...defaultCustomRecurrence,
      freq, interval, byDay, byMonthDay, byMonth,
      endType: endTypeMapped, until, count,
    })
  }, [repeatUnit, repeatFrequency, selectedWeekDays, selectedDays, selectedMonths, endType, endDate, endCount])

  const toggleWeekDay = (dayId: string) => {
    const idx = parseInt(dayId, 10)
    setSelectedWeekDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]
    )
  }

  const toggleMonthDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    )
  }

  const toggleMonth = (month: string) => {
    setSelectedMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    )
  }

  const intervalSuffix = (n: number) => {
    const u = repeatUnit.id
    if (u === 'day') return ' ' + t(n > 1 ? 'recurrenceComponent.days' : 'recurrenceComponent.day')
    if (u === 'week') return ' ' + t(n > 1 ? 'recurrenceComponent.weeks' : 'recurrenceComponent.week')
    if (u === 'month') return ' ' + t('recurrenceComponent.month')
    if (u === 'year') return ' ' + t(n > 1 ? 'recurrenceComponent.years' : 'recurrenceComponent.year')
    return ''
  }

  const intervalOptions = [1, 2, 3, 4, 5, 6].map((n) => ({
    id: String(n),
    name: String(n) + intervalSuffix(n),
  }))

  const countOptions = Array.from({ length: 10 }, (_, i) => i + 1).map((n) => ({
    id: String(n),
    name: n + ' ' + t(n > 1 ? 'recurrenceComponent.repetitions' : 'recurrenceComponent.repetition'),
  }))

  return (
    <>
      <ScrollView showsVerticalScrollIndicator={false} style={styles.container}>
        <View style={styles.frequencyContainer}>
          <View style={styles.frequencyRow}>
            <Text type="bodySmall" style={styles.flex}>{t('recurrenceComponent.recurrence')}</Text>
            <ContextualDropdown
              data={FREQ_OPTIONS}
              label={repeatUnit.name}
              selectedId={repeatUnit.id}
              onSelect={(id) => { const item = FREQ_OPTIONS.find(o => o.id === id); if (item) setRepeatUnit(item) }}
              buttonStyle={styles.unitButton}
            />
          </View>
          <View style={styles.frequencyRow}>
            <Text type="bodySmall" style={styles.flex}>{t('recurrenceComponent.every')}</Text>
            <ContextualDropdown
              data={intervalOptions}
              label={repeatFrequency.id + intervalSuffix(parseInt(repeatFrequency.id, 10) || 1)}
              selectedId={repeatFrequency.id}
              onSelect={(id) => { const item = intervalOptions.find(o => o.id === id); if (item) setRepeatFrequency(item) }}
              buttonStyle={styles.unitButton}
            />
          </View>
        </View>

        {repeatUnit.id !== 'day' && (
          <View style={styles.dayContainer}>
            {repeatUnit.id === 'week' && (
              <>
                <Text type="bodySmall" style={styles.sectionTitle}>{t('recurrenceComponent.repeatOn')}</Text>
                <View style={styles.daysRow}>
                  {weekDays.map((day) => (
                    <TouchableOpacity
                      key={day.id}
                      style={[styles.dayButton, selectedWeekDays.includes(parseInt(day.id, 10)) && styles.selectedDay]}
                      onPress={() => toggleWeekDay(day.id)}
                    >
                      <Text
                        type="bodySmall"
                        style={selectedWeekDays.includes(parseInt(day.id, 10)) ? styles.selectedDayText : styles.dayText}
                      >
                        {day.name.slice(0, 3)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}
            {repeatUnit.id === 'year' && (
              <View style={styles.monthDaysContainer}>
                {MONTH_LABELS.map((month) => (
                  <TouchableOpacity
                    key={month}
                    style={[styles.monthButton, selectedMonths.includes(month) && styles.selectedMonthDay]}
                    onPress={() => toggleMonth(month)}
                  >
                    <RNText style={[styles.monthDayText, { width: 35 }]}>{month}</RNText>
                  </TouchableOpacity>
                ))}
              </View>
            )}
            {repeatUnit.id === 'year' && <Text type="bodySmall" style={styles.sectionTitle}>{t('recurrenceComponent.monthDetail')}</Text>}
            {['month', 'year'].includes(repeatUnit.id) && (
              <View style={styles.monthDaysContainer}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <TouchableOpacity
                    key={day}
                    style={[styles.monthDayButton, selectedDays.includes(day) && styles.selectedMonthDay]}
                    onPress={() => toggleMonthDay(day)}
                  >
                    <Text type="bodySmall" style={styles.monthDayText}>{day}</Text>
                  </TouchableOpacity>
                ))}
                <TouchableOpacity
                  style={[styles.lastDayButton, selectedDays.includes(32) && styles.selectedMonthDay]}
                  onPress={() => toggleMonthDay(32)}
                >
                  <Text type="bodySmall" style={styles.monthDayText}>{t('recurrenceComponent.lastDayOfMonth')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        <Text type="body" style={styles.sectionTitle}>{t('recurrenceComponent.end')}</Text>
        <View style={styles.endOptionsContainer}>
          <TouchableOpacity style={styles.radioOption} onPress={() => setEndType('Jamais')}>
            <View style={[styles.radioButton, endType === 'Jamais' && styles.radioSelected]}>
              {endType === 'Jamais' && <View style={styles.radioInner} />}
            </View>
            <Text type="bodySmall" style={styles.radioText}>{t('recurrenceComponent.never')}</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.radioOption} onPress={() => setEndType('Le')}>
            <View style={[styles.radioButton, endType === 'Le' && styles.radioSelected]}>
              {endType === 'Le' && <View style={styles.radioInner} />}
            </View>
            <Text type="bodySmall" style={styles.radioText}>{t('recurrenceComponent.on')}</Text>
            <TouchableOpacity style={styles.dateButton} onPress={() => setIsDatePickerModalVisible(true)}>
              <Text type="bodySmall" style={styles.dateButtonText} color={theme.colors.secondary}>
                {dayjs(endDate).format('dddd DD MMM YYYY')}
              </Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.radioOption} onPress={() => setEndType('Après')}>
            <View style={[styles.radioButton, endType === 'Après' && styles.radioSelected]}>
              {endType === 'Après' && <View style={styles.radioInner} />}
            </View>
            <Text type="bodySmall" style={styles.radioText}>{t('recurrenceComponent.after')}</Text>
            <TouchableOpacity style={styles.dateButton} onPress={() => setIsPickerEndRepeatModalVisible(true)}>
              <Text type="bodySmall" style={styles.dateButtonText} color={theme.colors.secondary}>
                {endCount.id} {t(parseInt(endCount.id) > 1 ? 'recurrenceComponent.repetitions' : 'recurrenceComponent.repetition')}
              </Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </View>
      </ScrollView>
      <PickerModal
        isVisible={isPickerEndRepeatModalVisible}
        setIsVisible={setIsPickerEndRepeatModalVisible}
        value={endCount}
        onSelect={setEndCount}
        data={countOptions}
      />
      <DatePicker
        modal
        mode="date"
        date={endDate}
        open={isDatePickerModalVisible}
        onConfirm={(selectedDate) => {
          setEndDate(selectedDate)
          setIsDatePickerModalVisible(false)
        }}
        onCancel={() => setIsDatePickerModalVisible(false)}
      />
    </>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: { width: '100%', paddingHorizontal: theme.spacing.md, backgroundColor: theme.colors.grey10, borderRadius: theme.spacing.lg, marginTop: theme.spacing.md },
    sectionTitle: { marginVertical: theme.spacing.md },
    frequencyContainer: { marginTop: theme.spacing.lg },
    frequencyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing.md,
    },
    unitButton: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      borderRadius: 25,
      borderColor: '#C7C7CC',
      backgroundColor: 'white',
    },
    dayContainer: { marginVertical: theme.spacing.md },
    daysRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 },
    dayButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: theme.colors.greyBlue,
      justifyContent: 'center',
      alignItems: 'center',
    },
    selectedDay: { borderColor: theme.colors.primary },
    dayText: { fontSize: 12 },
    selectedDayText: { color: theme.colors.primary },
    endOptionsContainer: { marginBottom: 24 },
    radioOption: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
    radioButton: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.colors.greyBlue,
      marginRight: 12,
      justifyContent: 'center',
      alignItems: 'center',
    },
    radioSelected: { borderColor: theme.colors.primary },
    radioInner: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: theme.colors.primary,
    },
    radioText: { flex: 1, color: '#000', marginRight: 12 },
    dateButton: {
      flex: 2,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 6,
    },
    dateButtonText: { fontSize: 14 },
    monthDaysContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    monthDayButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 8,
    },
    monthButton: {
      height: 40,
      borderRadius: 20,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 8,
      paddingHorizontal: theme.spacing.xl,
    },
    selectedMonthDay: {
      borderWidth: 1,
      backgroundColor: theme.colors.primary + '10',
      borderColor: theme.colors.primary,
    },
    monthDayText: { fontSize: 14 },
    lastDayButton: {
      borderWidth: 1,
      borderColor: theme.colors.greyBlue,
      borderRadius: 20,
      paddingHorizontal: theme.spacing.sm,
      height: 40,
      justifyContent: 'center',
    },
    flex: { flex: 1 },
  })
